import { Button } from '@/global/components/Button'
import Menu from '@/global/components/MenuV2'
import { cn } from '@/lib/utils'
import { ChevronDownIcon } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'

export type MultipleOption = {
    /** Texto do item no menu */
    label: ReactNode
    /** Texto do botão principal quando esta é a opção primária (1ª). Default: label */
    buttonLabel?: ReactNode
    icon?: ReactNode
    onClick?: (event: MouseEvent<HTMLElement>) => void
    href?: string
    disabled?: boolean
}

interface Props {
    /** Primeira opção vira a ação do botão principal; todas aparecem no menu */
    options: MultipleOption[]
    disabled?: boolean
    variant?: 'contained' | 'outlined'
    /** Barra de progresso (0-100) na base do botão; null/undefined oculta */
    progress?: number | null
    className?: string
    id?: string
}

export function MultipleOptionButton({
    options,
    disabled,
    variant = 'contained',
    progress,
    className,
    id,
}: Props) {
    const primary = options[0]
    if (!primary) return null

    const hasMenu = options.length > 1

    return (
        <Menu.Root className="relative" position="right">
            <div
                className={cn(
                    'flex items-center rounded-sm overflow-hidden relative',
                    '[&_button]:border-0! [&_button]:rounded-none',
                    'h-10',
                    className,
                )}
            >
                <Button
                    id={id}
                    variant={variant}
                    onClick={primary.onClick}
                    href={primary.href}
                    disabled={disabled || primary.disabled}
                    startIcon={
                        primary.icon && (
                            <span className="flex [&_svg]:size-5">
                                {primary.icon}
                            </span>
                        )
                    }
                    className="flex-1 justify-between truncate h-full"
                >
                    {primary.buttonLabel ?? primary.label}
                </Button>
                {hasMenu && (
                    <>
                        <div className="self-stretch border-l" />
                        <Menu.Trigger>
                            <Button
                                variant={variant}
                                size="small"
                                disabled={disabled}
                                aria-label="Mais opções"
                                className="min-w-0 px-1 h-full"
                            >
                                <ChevronDownIcon className="size-7" />
                            </Button>
                        </Menu.Trigger>
                    </>
                )}
                {progress != null && (
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10">
                        <div
                            className="h-full bg-current transition-[width]"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}
            </div>
            {hasMenu && (
                <Menu.List>
                    {options.slice(1).map((option, index) => (
                        <Menu.Item
                            key={index}
                            onClick={option.onClick}
                            href={option.href}
                            disabled={option.disabled}
                            className="gap-2 [&_svg]:size-5"
                        >
                            {option.icon}
                            {option.label}
                        </Menu.Item>
                    ))}
                </Menu.List>
            )}
        </Menu.Root>
    )
}

export default MultipleOptionButton
